"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ExplanationSource = exports.ExplanationComparator = exports.ExcludedDebtReason = exports.DeliveredLineSizeSource = exports.LineSizeLimitingFactor = exports.LineSizeLimitCap = exports.LineSizeLimitCategory = exports.UnderwritingRatio = exports.ExplanationValueUnit = exports.UnderwritingOptionDimensionKey = void 0;
/**
 * Declared in the order ops agents read an offer in; Python emits dimensions in this order and the
 * explorer lays options out by it.
 *
 * @TranslateToPython
 */
var UnderwritingOptionDimensionKey;
(function (UnderwritingOptionDimensionKey) {
    UnderwritingOptionDimensionKey["fixedTerm"] = "fixedTerm";
    UnderwritingOptionDimensionKey["firstDrawFee"] = "firstDrawFee";
    UnderwritingOptionDimensionKey["liensToRefi"] = "liensToRefi";
    UnderwritingOptionDimensionKey["refiPayoffTerm"] = "refiPayoffTerm";
    UnderwritingOptionDimensionKey["lineSizeRange"] = "lineSizeRange";
    UnderwritingOptionDimensionKey["propertyJurisdiction"] = "propertyJurisdiction";
    UnderwritingOptionDimensionKey["occupancy"] = "occupancy";
})(UnderwritingOptionDimensionKey || (exports.UnderwritingOptionDimensionKey = UnderwritingOptionDimensionKey = {}));
/** @TranslateToPython */
var ExplanationValueUnit;
(function (ExplanationValueUnit) {
    ExplanationValueUnit["dollars"] = "dollars";
    /** A decimal ratio, e.g. 0.45 for a 45% DTI. */
    ExplanationValueUnit["ratio"] = "ratio";
    ExplanationValueUnit["creditScore"] = "creditScore";
    ExplanationValueUnit["quantity"] = "quantity";
    ExplanationValueUnit["months"] = "months";
    ExplanationValueUnit["text"] = "text";
})(ExplanationValueUnit || (exports.ExplanationValueUnit = ExplanationValueUnit = {}));
/** @TranslateToPython */
var UnderwritingRatio;
(function (UnderwritingRatio) {
    UnderwritingRatio["debtToIncome"] = "debtToIncome";
    UnderwritingRatio["combinedLoanToValue"] = "combinedLoanToValue";
})(UnderwritingRatio || (exports.UnderwritingRatio = UnderwritingRatio = {}));
/**
 * What a line size limit is about, in terms that do not name internal models or limiters.
 *
 * @TranslateToPython
 */
var LineSizeLimitCategory;
(function (LineSizeLimitCategory) {
    LineSizeLimitCategory["requestedAmount"] = "requestedAmount";
    LineSizeLimitCategory["income"] = "income";
    LineSizeLimitCategory["homeEquity"] = "homeEquity";
    LineSizeLimitCategory["creditProfile"] = "creditProfile";
    LineSizeLimitCategory["creditScore"] = "creditScore";
    LineSizeLimitCategory["recentCreditActivity"] = "recentCreditActivity";
    LineSizeLimitCategory["pastCreditEvents"] = "pastCreditEvents";
    LineSizeLimitCategory["stateRules"] = "stateRules";
    LineSizeLimitCategory["homeValuation"] = "homeValuation";
    LineSizeLimitCategory["programMaximum"] = "programMaximum";
})(LineSizeLimitCategory || (exports.LineSizeLimitCategory = LineSizeLimitCategory = {}));
/**
 * What held a line size limit below its own capacity.
 *
 * @TranslateToPython
 */
var LineSizeLimitCap;
(function (LineSizeLimitCap) {
    /** Small-line rules only apply up to the large-line threshold, so the limit stops there. */
    LineSizeLimitCap["largeLineThreshold"] = "largeLineThreshold";
})(LineSizeLimitCap || (exports.LineSizeLimitCap = LineSizeLimitCap = {}));
/** @TranslateToPython */
var LineSizeLimitingFactor;
(function (LineSizeLimitingFactor) {
    /** A line size limit (`bindingLimitName`) caps the line. */
    LineSizeLimitingFactor["lineSizeLimit"] = "lineSizeLimit";
    /** The line reached the largest line this option offers; no limit on the customer was lower. */
    LineSizeLimitingFactor["optionMaximum"] = "optionMaximum";
    /** The next pricing band's higher APR makes its first line size unaffordable; see `nextPricingBandBindingLimit`. */
    LineSizeLimitingFactor["nextPricingBandUnaffordable"] = "nextPricingBandUnaffordable";
    /** No APR is published for larger lines, e.g. combined loan-to-value beyond the pricing grid. */
    LineSizeLimitingFactor["pricingUnavailable"] = "pricingUnavailable";
})(LineSizeLimitingFactor || (exports.LineSizeLimitingFactor = LineSizeLimitingFactor = {}));
/**
 * Which line the offer was delivered and checked at.
 *
 * @TranslateToPython
 */
var DeliveredLineSizeSource;
(function (DeliveredLineSizeSource) {
    /** The largest eligible line. */
    DeliveredLineSizeSource["maxEligible"] = "maxEligible";
    /** The line that pays out the amount the customer asked for. */
    DeliveredLineSizeSource["statedUsage"] = "statedUsage";
    /** The line that pays out the amount asked for plus the liens being refinanced. */
    DeliveredLineSizeSource["statedUsagePlusRefi"] = "statedUsagePlusRefi";
    /** The amount asked for needs less than the smallest eligible line, so the offer starts there. */
    DeliveredLineSizeSource["minEligible"] = "minEligible";
})(DeliveredLineSizeSource || (exports.DeliveredLineSizeSource = DeliveredLineSizeSource = {}));
/** @TranslateToPython */
var ExcludedDebtReason;
(function (ExcludedDebtReason) {
    /** A lien the line pays off. */
    ExcludedDebtReason["refinanced"] = "refinanced";
    /** Debts the line pays off through a debt swap. */
    ExcludedDebtReason["debtSwap"] = "debtSwap";
})(ExcludedDebtReason || (exports.ExcludedDebtReason = ExcludedDebtReason = {}));
/** @TranslateToPython */
var ExplanationComparator;
(function (ExplanationComparator) {
    ExplanationComparator["lt"] = "lt";
    ExplanationComparator["lte"] = "lte";
    ExplanationComparator["gt"] = "gt";
    ExplanationComparator["gte"] = "gte";
    ExplanationComparator["eq"] = "eq";
    ExplanationComparator["neq"] = "neq";
})(ExplanationComparator || (exports.ExplanationComparator = ExplanationComparator = {}));
/** @TranslateToPython */
var ExplanationSource;
(function (ExplanationSource) {
    /** Built while underwriting ran, from everything underwriting evaluated. */
    ExplanationSource["recordedAtUnderwriting"] = "recordedAtUnderwriting";
    /**
     * Rebuilt later from the stored analysis info of a run that predates the current explanation.
     * The stored frames have each limit, outcome, and ratio, but not the values hardcuts compared,
     * the APR steps, or the ratio maximums in effect, so those parts are empty.
     */
    ExplanationSource["reconstructedFromAnalysisInfo"] = "reconstructedFromAnalysisInfo";
})(ExplanationSource || (exports.ExplanationSource = ExplanationSource = {}));
